import { useEffect, useRef, useState } from "react"
import { useAccessibility } from "../context/AccessibilityContext"
import { X, Plus, Minus } from "lucide-react"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUniversalAccess } from '@fortawesome/free-solid-svg-icons'

/* Ligne de réglage avec stepper - / valeur / + */
const StepperRow = ({ label, value, onDec, onInc, decLabel, incLabel }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
    <p className="text-sm font-semibold text-[#cbbef0] mb-2">{label}</p>
    <div className="flex items-center justify-between">
      <button onClick={onDec} aria-label={decLabel}
        className="grid place-items-center h-9 w-9 rounded-xl glass text-white hover:bg-white/10 transition active:scale-95">
        <Minus size={16} />
      </button>
      <span className="font-display font-bold text-white tabular-nums">{value}</span>
      <button onClick={onInc} aria-label={incLabel}
        className="grid place-items-center h-9 w-9 rounded-xl glass text-white hover:bg-white/10 transition active:scale-95">
        <Plus size={16} />
      </button>
    </div>
  </div>
)

/* Ligne de réglage avec interrupteur on/off */
const ToggleRow = ({ label, active, onToggle }) => (
  <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.06] p-4">
    <p className="text-sm font-semibold text-[#cbbef0]">{label}</p>
    <button
      onClick={onToggle}
      role="switch"
      aria-checked={active}
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full p-1 transition-colors ${active ? 'bg-grad-violet' : 'bg-white/40'}`}
    >
      <span className={`h-5 w-5 rounded-full bg-white shadow transition-transform duration-300 ${active ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  </div>
)

function ModuleAccessibility() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const wasOpenRef = useRef(false);
  const [fontSizeName, setFontSizeName] = useState('100%');
  const [lineHeightName, setLineHeightName] = useState('Normal');
  const [letterSpacings, setLetterSpacing] = useState('Normal');
  const {
    fontSize, increaseFontSize, decreaseFontSize,
    lineHeight, increaseLineHeight, decreaseLineHeight,
    increaseLetterSpacing, decreaseLetterSpacing, letterSpacing,
    highlightLinks, toggleHighlightLinks,
    cursorStyle, setCursorBlackLarge, setCursorWhiteLarge,
    motionPaused, toggleMotion,
  } = useAccessibility();

  const handleClick = () => setIsModalOpen((v) => !v)

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setIsModalOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Gestion du focus : à l'ouverture, on entre dans le panneau ; à la fermeture,
  // on rend le focus au bouton déclencheur (RGAA 7.x — clavier / lecteurs d'écran).
  useEffect(() => {
    if (isModalOpen) {
      panelRef.current?.focus()
    } else if (wasOpenRef.current) {
      triggerRef.current?.focus()
    }
    wasOpenRef.current = isModalOpen
  }, [isModalOpen])

  useEffect(() => {
    const map = { 'text-sm': '90%', 'text-base': '100%', 'text-lg': '120%', 'text-xl': '140%' }
    setFontSizeName(map[fontSize] || '100%')
  }, [fontSize])

  useEffect(() => {
    const map = { 'line-height-base': 'Normal', 'line-height-md': 'x2', 'line-height-lg': 'x3' }
    setLineHeightName(map[lineHeight] || 'Normal')
  }, [lineHeight])

  useEffect(() => {
    const map = { 'letter-spacing-base': 'Normal', 'letter-spacing-md': 'Moyen', 'letter-spacing-lg': 'Large' }
    setLetterSpacing(map[letterSpacing] || 'Normal')
  }, [letterSpacing])

  return (
    <>
      {/* Bouton flottant */}
      <button
        ref={triggerRef}
        onClick={handleClick}
        aria-label="Ouvrir le module d'accessibilité"
        aria-expanded={isModalOpen}
        className="a11y-immune fixed bottom-6 right-6 z-[65] grid place-items-center h-14 w-14 rounded-full overflow-hidden bg-gradient-to-br from-[#4f6bf0] to-[#3a32a0] shadow-glow-azure transition-transform hover:scale-110 active:scale-95"
      >
        {/* Disque blanc qui remplit le bouton ; le personnage laisse voir le bleu du fond */}
        <FontAwesomeIcon icon={faUniversalAccess} className="text-white text-[58px] leading-none" />
      </button>

      {/* Panneau */}
      {isModalOpen && (
        <div
          ref={panelRef}
          tabIndex={-1}
          role="dialog"
          aria-label="Module d'accessibilité"
          className="a11y-immune fixed z-[66] bottom-24 right-4 sm:right-6 w-[min(92vw,22rem)] max-h-[72vh] overflow-y-auto no-scrollbar bg-[#1a0e3d] border border-white/15 rounded-3xl p-6 shadow-glow outline-none"
        >
          <div className="flex items-start justify-between mb-1">
            <div>
              <h2 className="font-display font-extrabold text-2xl">Accessibilité</h2>
              <p className="text-sm text-[#a99bd0] mt-1">Adaptez l'affichage à votre confort.</p>
            </div>
            <button
              onClick={() => setIsModalOpen(false)}
              aria-label="Fermer le module d'accessibilité"
              className="grid place-items-center h-9 w-9 rounded-full glass text-white hover:bg-white/10 transition shrink-0"
            >
              <X size={18} />
            </button>
          </div>

          <div className="mt-5 space-y-3">
            <StepperRow label="Taille de la police" value={fontSizeName}
              onDec={decreaseFontSize} onInc={increaseFontSize}
              decLabel="Réduire la taille de police" incLabel="Augmenter la taille de police" />
            <StepperRow label="Hauteur de ligne" value={lineHeightName}
              onDec={decreaseLineHeight} onInc={increaseLineHeight}
              decLabel="Réduire la hauteur de ligne" incLabel="Augmenter la hauteur de ligne" />
            <StepperRow label="Espacement des lettres" value={letterSpacings}
              onDec={decreaseLetterSpacing} onInc={increaseLetterSpacing}
              decLabel="Réduire l'espacement des lettres" incLabel="Augmenter l'espacement des lettres" />
            <ToggleRow label="Figer les animations" active={motionPaused} onToggle={toggleMotion} />
            <ToggleRow label="Liens en surbrillance" active={highlightLinks} onToggle={toggleHighlightLinks} />
            <ToggleRow label="Grand curseur clair" active={cursorStyle === 'white-large'} onToggle={setCursorWhiteLarge} />
            <ToggleRow label="Grand curseur sombre" active={cursorStyle === 'black-large'} onToggle={setCursorBlackLarge} />
          </div>
        </div>
      )}
    </>
  )
}

export default ModuleAccessibility
